
// Export-value/data-source layer (task: "CSV-generatoren bør i princippet
// kun have ansvar for: profil + features -> outputrækker -> CSV. Kendskabet
// til PointViews forskellige værdier bør ligge i et passende export-value/
// data-source-lag."). Everything the CSV generator (csvGenerator.js) needs
// to know about a PointView Feature lives here, behind stable, UI-label-
// independent source ids ("feature.name", "geometry.length",
// "attribute:dimensionMm", "constant") - csvGenerator.js never branches on
// a column's HEADER or on which tool produced a feature.
import {totalPolylineLength, polygonArea, polygonPerimeter} from "../../geometry/measurementGeometry.js";

import {t, T, localized} from "../../i18n/index.js";
// Same "flatten this feature's geometry to a plain [x,y,z][] list" shape
// FeatureVisual.rawCoordinates()/the exporters' own flattenCoordinates()
// already use - kept here as this layer's own copy since it's pure
// Feature-model math with no THREE.js/DOM dependency, unlike
// FeatureVisual's version.
export function rawCoordinates(feature){
	const type = feature.geometry.type;
	const coords = feature.geometry.coordinates;
	if(type === "Point") return [coords];
	if(type === "LineString") return coords;
	if(type === "Polygon") return coords[0] || [];
	return [];
}

// One row-context per vertex, 1-based index (matches every existing export
// format's own vertex numbering convention - see CSVFeatureExporter's old
// flattenCoordinates()/ShapefileFeatureExporter/DXFFeatureExporter).
export function buildVertexContexts(feature){
	return rawCoordinates(feature).map((vertex, i) => ({vertexIndex: i + 1, vertex}));
}

// --- Feature group: PointView's own generic Feature-record fields. A
// closed, bounded set of the fields every feature already has regardless
// of tool (Feature.js) - not a per-tool special case. ---
export const FEATURE_FIELDS = localized([
	{field: "id", label: T("Objekt-ID")}, // (ID-kolonnen med fortløbende nummer hedder "ID" - se datakilden "rownumber")
	{field: "name", label: T("Navn")},
	{field: "category", label: T("Kategori")},
	{field: "tool", label: T("Værktøj")},
	{field: "featureType", label: T("Featuretype")},
	{field: "geometryType", label: T("Geometritype")},
	{field: "description", label: T("Beskrivelse")},
	{field: "created", label: T("Oprettet")},
	{field: "modified", label: T("Ændret")},
]);

function getFeatureFieldValue(feature, field){
	switch(field){
		case "id": return feature.id;
		case "name": return feature.properties.name;
		case "category": return feature.properties.category;
		case "tool": return feature.properties.tool;
		case "featureType": return feature.properties.featureType;
		case "geometryType": return feature.geometry.type;
		case "description": return feature.properties.description;
		case "created": return feature.metadata.created;
		case "modified": return feature.metadata.modified;
		default: return undefined;
	}
}

// --- Geometry group. `vertexOnly` fields only have a defined meaning in
// vertex output-mode (task section "Én række pr. objekt": "Et generisk
// X/Y/Z må eksempelvis ikke tilfældigt betyde første vertex") - resolved as
// missing (-> fallback/empty) when used outside a vertex row context,
// rather than silently guessing which vertex. Object-mode coordinate needs
// go through the explicit first/last/centroid fields instead, which are
// never ambiguous about which point they mean. `coordinate: true` fields
// are the ones a profile's "Koordinatdecimaler" precision setting applies
// to (task: precision is about coordinates specifically, not length/area). ---
export const GEOMETRY_FIELDS = localized([
	{field: "vertexIndex", label: T("Vertexnummer"), vertexOnly: true},
	{field: "x", label: "X", vertexOnly: true, coordinate: true},
	{field: "y", label: "Y", vertexOnly: true, coordinate: true},
	{field: "z", label: "Z", vertexOnly: true, coordinate: true},
	{field: "vertexCount", label: T("Antal punkter")},
	{field: "length", label: T("Længde (m)")},
	{field: "area", label: T("Areal (m²)")},
	{field: "perimeter", label: T("Omkreds (m)")},
	{field: "firstX", label: T("Startpunkt X"), coordinate: true},
	{field: "firstY", label: T("Startpunkt Y"), coordinate: true},
	{field: "firstZ", label: T("Startpunkt Z"), coordinate: true},
	{field: "lastX", label: T("Slutpunkt X"), coordinate: true},
	{field: "lastY", label: T("Slutpunkt Y"), coordinate: true},
	{field: "lastZ", label: T("Slutpunkt Z"), coordinate: true},
	{field: "centroidX", label: T("Centroide X"), coordinate: true},
	{field: "centroidY", label: T("Centroide Y"), coordinate: true},
	{field: "centroidZ", label: T("Centroide Z"), coordinate: true},
]);

export const COORDINATE_GEOMETRY_FIELDS = new Set(GEOMETRY_FIELDS.filter((f) => f.coordinate).map((f) => f.field));

function at(list, index, axis){
	if(!list || list.length === 0) return undefined;
	const point = index < 0 ? list[list.length + index] : list[index];
	return point ? point[axis] : undefined;
}

function centroidAxis(list, axis){
	if(!list || list.length === 0) return undefined;
	let sum = 0;
	for(const p of list) sum += p[axis] || 0;
	return sum / list.length;
}

function getGeometryFieldValue(feature, field, vertexCtx){
	const type = feature.geometry.type;
	switch(field){
		case "vertexIndex": return vertexCtx ? vertexCtx.vertexIndex : undefined;
		case "x": return vertexCtx ? vertexCtx.vertex[0] : undefined;
		case "y": return vertexCtx ? vertexCtx.vertex[1] : undefined;
		case "z": return vertexCtx ? vertexCtx.vertex[2] : undefined;
		case "vertexCount": return rawCoordinates(feature).length;
		case "length": return type === "LineString" ? totalPolylineLength(rawCoordinates(feature)) : undefined;
		case "area": return type === "Polygon" ? polygonArea(rawCoordinates(feature)) : undefined;
		case "perimeter": return type === "Polygon" ? polygonPerimeter(rawCoordinates(feature)) : undefined;
		case "firstX": return at(rawCoordinates(feature), 0, 0);
		case "firstY": return at(rawCoordinates(feature), 0, 1);
		case "firstZ": return at(rawCoordinates(feature), 0, 2);
		case "lastX": return at(rawCoordinates(feature), -1, 0);
		case "lastY": return at(rawCoordinates(feature), -1, 1);
		case "lastZ": return at(rawCoordinates(feature), -1, 2);
		case "centroidX": return centroidAxis(rawCoordinates(feature), 0);
		case "centroidY": return centroidAxis(rawCoordinates(feature), 1);
		case "centroidZ": return centroidAxis(rawCoordinates(feature), 2);
		default: return undefined;
	}
}

// --- Attributes group: fully dynamic, discovered from the actual features
// being exported (same technique CSVFeatureExporter/ShapefileFeatureExporter
// already used pre-this-task) - never a hardcoded key list, since "forskellige
// features kan have forskellige attributes" (task). ---
export function discoverAttributeKeys(features){
	const keys = new Set();
	for(const f of features){
		for(const key of Object.keys(f.properties.attributes || {})) keys.add(key);
	}
	return [...keys].sort();
}

function getAttributeValue(feature, key){
	return (feature.properties.attributes || {})[key];
}

// Cosmetic-only UI grouping/labels for attribute keys this codebase's own
// tools are known to produce (task's own example: "Header: PIPE_DIAMETER /
// Datakilde: Rør -> Dimension"). This is presentation metadata for the
// column/datasource picker - it never changes how a value is RESOLVED
// (getAttributeValue above is fully generic for every key, known or not),
// so an attribute from a future/unknown tool still works correctly, just
// grouped under the generic fallback group below.
const KNOWN_ATTRIBUTE_META = localized({
	dimensionMm: {group: T("Rør"), label: T("Dimension (mm)")},
	radius: {group: T("Rør"), label: T("Radius (m)")},
	innerRadius: {group: T("Rør"), label: T("Indvendig radius (m)")},
	wallThicknessMeters: {group: T("Rør"), label: T("Godstykkelse (m)")},
	centerZ: {group: T("Rør"), label: T("Centerkote (m)")},
	outerTopZ: {group: T("Rør"), label: T("Topkote, udvendig (m)")},
	outerBottomZ: {group: T("Rør"), label: T("Bundkote, udvendig (m)")},
	innerTopZ: {group: T("Rør"), label: T("Topkote, indvendig (m)")},
	innerBottomZ: {group: T("Rør"), label: T("Bundkote, indvendig (m)")},
	sectionLength: {group: T("Rør"), label: T("Analyseret længde (m)")},
	angularCoverageDeg: {group: T("Rør"), label: T("Dækningsvinkel (grader)")},
	quality: {group: T("Rør"), label: T("Kvalitet")},
	reducedAccuracy: {group: T("Rør"), label: T("Reduceret nøjagtighed")},
});
const FALLBACK_ATTRIBUTE_GROUP = T("Attributter");

export function getAttributeGroupLabel(key){
	const meta = KNOWN_ATTRIBUTE_META[key];
	return meta ? meta.group : t(FALLBACK_ATTRIBUTE_GROUP);
}

export function getAttributeDisplayLabel(key){
	const meta = KNOWN_ATTRIBUTE_META[key];
	return meta ? meta.label : key;
}

// --- Single generic resolver every column source kind goes through. ---
// source shapes:
//   {kind: "feature", field}
//   {kind: "geometry", field}
//   {kind: "attribute", key}
//   {kind: "rownumber"}        fortløbende nummer 1, 2, 3 ... på de skrevne rækker (uafhængigt af
//                                featuren; i vertex-tilstand tæller hver vertex-række)
//   {kind: "attributes-all"}   (expanded into concrete "attribute" columns
//                                by csvGenerator.js before rows are built -
//                                never resolved directly here)
//   {kind: "constant", value}
export function resolveSourceValue(source, ctx){
	if(!source) return undefined;
	const {feature, vertex} = ctx;
	switch(source.kind){
		case "constant": return source.value;
		case "rownumber": return ctx.rowNumber;
		case "feature": return getFeatureFieldValue(feature, source.field);
		case "geometry": return getGeometryFieldValue(feature, source.field, vertex);
		case "attribute": return getAttributeValue(feature, source.key);
		default: return undefined;
	}
}

// Attributter der er geometrisk beregnede/målte værdier (koordinater, radius/diameter, kote, længde, vinkel) - kun for disse
// (og for geometri-kilderne) begrænser CSV'en antallet af decimaler (se csvGenerator.js). ALLE andre attributter er brugerens/
// værktøjets egne data og skrives med deres oprindelige værdi/præcision, også selv om de er numeriske.
export const MEASURED_ATTRIBUTE_KEYS = new Set([
	"radius", "innerRadius", "dimensionMm", "wallThicknessMeters", "radiusM",
	"axisPoint", "axisDir", "station",
	"centerZ", "outerTopZ", "outerBottomZ", "innerTopZ", "innerBottomZ",
	"sectionLength", "angularCoverageDeg",
]);

// Er kildens værdi en geometrisk/målt værdi (-> højst 4 decimaler)? Geometri-felter altid; attributter kun de kendte målte.
export function isMeasuredSource(source){
	if(!source) return false;
	if(source.kind === "geometry") return true;
	if(source.kind === "attribute") return MEASURED_ATTRIBUTE_KEYS.has(source.key);
	return false;
}

export function isCoordinateGeometryField(field){
	return COORDINATE_GEOMETRY_FIELDS.has(field);
}
